import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  IconButton,
  Card,
  CardContent,
  Typography,
  alpha,
  useTheme,
  Chip,
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
  FiberManualRecord,
} from '@mui/icons-material';
import { renderIcon } from '../../../../helpers/renderDynamicIcons';//helpers/renderDynamicIcons
import { useTranslation } from 'react-i18next';

const CarouselTemplate = ({ 
  items, 
  onItemClick, 
  flatNavigationItems 
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [t] = useTranslation();
  const theme = useTheme();
  const carouselRef = useRef(null);

  const topLevelItems = items?.filter(item => !item.parentId) || [];
  const itemsPerView = 3;
  const maxIndex = Math.max(0, topLevelItems.length - itemsPerView);

  const getChildren = (item) => {
    if (item.children && Array.isArray(item.children)) {
      return item.children;
    }
    if (item.hasChildren && flatNavigationItems.length > 0) {
      return flatNavigationItems.filter(child => child.parentId === item.id);
    }
    return [];
  };

  const handlePrevious = () => {
    if (!isAnimating && currentIndex > 0) {
      setIsAnimating(true);
      setCurrentIndex(prev => prev - 1);
      setTimeout(() => setIsAnimating(false), 400);
    }
  };

  const handleNext = () => {
    if (!isAnimating && currentIndex < maxIndex) {
      setIsAnimating(true);
      setCurrentIndex(prev => prev + 1);
      setTimeout(() => setIsAnimating(false), 400);
    }
  };

  const handleDotClick = (index) => {
    if (!isAnimating) {
      setIsAnimating(true);
      setCurrentIndex(index);
      setTimeout(() => setIsAnimating(false), 400);
    }
  };

  const handleItemClickInternal = (item) => {
    const children = getChildren(item);
    if (children.length === 0) {
      onItemClick(item);
    }
  };

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: 1200,
        mx: 'auto',
        position: 'relative',
        py: 2,
      }}
    >
      {/* Navigation Buttons */}
      <IconButton
        onClick={handlePrevious}
        disabled={currentIndex === 0}
        sx={{
          position: 'absolute',
          left: -20,
          top: '50%',
          transform: 'translateY(-50%)',
          zIndex: 2,
          backgroundColor: theme.palette.background.paper,
          boxShadow: `0 4px 12px ${alpha(theme.palette.common.black, 0.15)}`,
          '&:hover': {
            backgroundColor: theme.palette.background.paper,
            transform: 'translateY(-50%) scale(1.1)',
          },
          '&.Mui-disabled': {
            opacity: 0.3,
          },
          transition: 'all 0.3s ease',
        }}
      >
        <ChevronLeft />
      </IconButton>

      <IconButton
        onClick={handleNext}
        disabled={currentIndex >= maxIndex}
        sx={{
          position: 'absolute',
          right: -20,
          top: '50%',
          transform: 'translateY(-50%)',
          zIndex: 2,
          backgroundColor: theme.palette.background.paper,
          boxShadow: `0 4px 12px ${alpha(theme.palette.common.black, 0.15)}`,
          '&:hover': {
            backgroundColor: theme.palette.background.paper,
            transform: 'translateY(-50%) scale(1.1)',
          },
          '&.Mui-disabled': {
            opacity: 0.3,
          },
          transition: 'all 0.3s ease',
        }}
      >
        <ChevronRight />
      </IconButton>

      {/* Carousel Container */}
      <Box
        ref={carouselRef}
        sx={{
          overflow: 'hidden',
          px: 2,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
            transform: `translateX(-${currentIndex * (100 / itemsPerView)}%)`,
            gap: 2,
          }}
        >
          {topLevelItems.map((item) => {
            const children = getChildren(item);
            const hasChildren = children.length > 0;

            return (
              <Card
                key={item.id}
                onClick={() => handleItemClickInternal(item)}
                sx={{
                  minWidth: `calc(${100 / itemsPerView}% - 16px)`,
                  cursor: hasChildren ? 'default' : 'pointer',
                  position: 'relative',
                  overflow: 'visible',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: `0 12px 24px ${alpha(theme.palette.primary.main, 0.2)}`,
                    '& .card-overlay': {
                      opacity: 1,
                    },
                  },
                }}
              >
                {/* Gradient Overlay */}
                <Box
                  className="card-overlay"
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
                    opacity: 0,
                    transition: 'opacity 0.3s ease',
                    pointerEvents: 'none',
                    zIndex: 1,
                  }}
                />

                <CardContent sx={{ position: 'relative', zIndex: 2, p: 3 }}>
                  {/* Icon Section */}
                  <Box
                    sx={{
                      width: 64,
                      height: 64,
                      borderRadius: 3,
                      background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 2,
                      boxShadow: `0 8px 16px ${alpha(theme.palette.primary.main, 0.3)}`,
                    }}
                  >
                    {renderIcon(item.icon, {
                      sx: { fontSize: 32, color: 'white' }
                    })}
                  </Box>

                  {/* Title */}
                  <Typography
                    variant="h6"
                    fontWeight={600}
                    sx={{
                      mb: 1,
                      color: theme.palette.text.primary,
                    }}
                  >
                    {t(item.name)}
                  </Typography>

                  {/* Description */}
                  {item.description && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        mb: 2,
                        minHeight: 40,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                      }}
                    >
                      {t(item.description)}
                    </Typography>
                  )}

                  {/* Children Count Badge */}
                  {hasChildren && (
                    <Chip
                      label={`${children.length} ${children.length === 1 ? 'item' : 'items'}`}
                      size="small"
                      sx={{
                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                        color: theme.palette.primary.main,
                        fontWeight: 600,
                        mt: 1,
                      }}
                    />
                  )}

                  {/* Sub-items Preview */}
                  {hasChildren && (
                    <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {children.slice(0, 3).map((child) => (
                        <Box
                          key={child.id}
                          onClick={(e) => {
                            e.stopPropagation();
                            onItemClick(child);
                          }}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            p: 1,
                            borderRadius: 1,
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              backgroundColor: alpha(theme.palette.primary.main, 0.08),
                              transform: 'translateX(4px)',
                            },
                          }}
                        >
                          {renderIcon(child.icon, {
                            sx: { fontSize: 16, color: theme.palette.text.secondary }
                          })}
                          <Typography variant="caption" color="text.secondary">
                            {t(child.name)}
                          </Typography>
                        </Box>
                      ))}
                      {children.length > 3 && (
                        <Typography
                          variant="caption"
                          sx={{
                            color: theme.palette.primary.main,
                            fontWeight: 600,
                            pl: 1,
                          }}
                        >
                          +{children.length - 3} more
                        </Typography>
                      )}
                    </Box>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </Box>
      </Box>

      {/* Dots Indicator */}
      {topLevelItems.length > itemsPerView && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            gap: 1,
            mt: 3,
          }}
        >
          {Array.from({ length: maxIndex + 1 }).map((_, index) => (
            <IconButton
              key={index}
              onClick={() => handleDotClick(index)}
              size="small"
              sx={{
                p: 0.5,
                '&:hover': {
                  backgroundColor: 'transparent',
                },
              }}
            >
              <FiberManualRecord
                sx={{
                  fontSize: currentIndex === index ? 12 : 8,
                  color: currentIndex === index
                    ? theme.palette.primary.main
                    : alpha(theme.palette.text.secondary, 0.3),
                  transition: 'all 0.3s ease',
                }}
              />
            </IconButton>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default CarouselTemplate;
